import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { TileEmpty } from '../TileWrapper';
import { Users, Shield, UserCog, User } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  total: 24,
  admins: 2,
  managers: 5,
  users: 17,
};

export function TeamOverviewTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: members, error } = await supabase
          .from('company_members')
          .select('role')
          .eq('company_id', company.id);

        if (error) throw error;

        const counts = {
          total: members?.length || 0,
          admins: members?.filter(m => m.role === 'Admin').length || 0,
          managers: members?.filter(m => m.role === 'Manager').length || 0,
          users: members?.filter(m => m.role === 'User').length || 0,
        };

        setData(counts);
      } catch (err) {
        console.error('Failed to load team overview:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto mb-2" />
          <div className="h-4 w-20 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return <TileEmpty message="No team members" icon={Users} />;
  }

  if (size === 'small') {
    return (
      <div
        onClick={() => navigate('/company-team')}
        className="flex flex-col items-center justify-center h-full text-center cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
      >
        <div className="bg-blue-100 p-3 rounded-full mb-2">
          <Users className="h-6 w-6 text-blue-600" />
        </div>
        <p className="text-3xl font-bold text-gray-900">{data.total}</p>
        <p className="text-sm text-gray-600">Team Members</p>
      </div>
    );
  }

  const roles = [
    { key: 'admins', label: 'Admins', icon: Shield, color: 'text-red-600', bg: 'bg-red-100' },
    { key: 'managers', label: 'Managers', icon: UserCog, color: 'text-blue-600', bg: 'bg-blue-100' },
    { key: 'users', label: 'Users', icon: User, color: 'text-gray-600', bg: 'bg-gray-100' },
  ];

  return (
    <div
      onClick={() => navigate('/company-team')}
      className="h-full flex flex-col cursor-pointer hover:bg-gray-50 rounded-lg transition-colors p-2 -m-2"
    >
      <div className="flex items-center justify-center gap-2 mb-4">
        <Users className="h-5 w-5 text-blue-600" />
        <span className="text-2xl font-bold">{data.total}</span>
        <span className="text-gray-600">Members</span>
      </div>

      <div className="flex-1 flex gap-2 justify-center">
        {roles.map((role) => {
          const Icon = role.icon;
          const count = data[role.key];

          return (
            <div key={role.key} className="text-center">
              <div className={cn("w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-1", role.bg)}>
                <Icon className={cn("h-5 w-5", role.color)} />
              </div>
              <p className="text-lg font-semibold">{count}</p>
              <p className="text-xs text-gray-500">{role.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
